import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Container, TextField, Typography } from '@mui/material';
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import Navbar from '../components/Navbar';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { fetchUserLanguage } from '../hooks/UserLanguage';

const RegisterPage: React.FC = () => {
  const { REACT_APP_API_URL } = process.env;
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordRepeat, setPasswordRepeat] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const signIn = useSignIn();
  const { t } = useTranslation();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!username || !password || !passwordRepeat || !email) {
      setError(t('register.fillFields'));
      setLoading(false);
      return;
    }

    if (password !== passwordRepeat) {
      setError(t('register.passwordMismatch'));
      setPasswordRepeat('');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${REACT_APP_API_URL}/users/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user: { username, email, password } }),
      });
      const data = await response.json();

      if (response.ok) {
        try {
          const loginResponse = await fetch(`${REACT_APP_API_URL}/api/token/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          });
          const loginData = await loginResponse.json();

          if (loginResponse.ok) {
            signIn({
              auth: {
                token: loginData.access,
                type: 'Bearer',
              },
            });
            localStorage.setItem('username', username);
            localStorage.setItem('refreshToken', loginData.refresh);
            const userLang = await fetchUserLanguage(loginData.access);
            if (userLang) {
              await i18n.changeLanguage(userLang);
            }
            navigate('/');
          } else {
            navigate('/login');
          }
        } catch {
          navigate('/login');
        }
      } else {
        if (data && data.user) {
          const allErrorsCombined = Object.values(data.user).flat().join('\n');
          setError(`${t('register.failMessage')}\n\n${allErrorsCombined}`);
        }
      }
    } catch (error) {
      const errorMessage =
        (error as Error).message || t('register.unknownError');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Navbar />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h5">
            {t('register.title')}
          </Typography>
          <Box component="form" onSubmit={handleRegister} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              label={t('register.username')}
              name="username"
              required
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              label={t('register.email')}
              name="email"
              required
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              name="password"
              required
              label={t('register.password')}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              name="passwordRepeat"
              required
              label={t('register.passwordRepeat')}
              type="password"
              value={passwordRepeat}
              onChange={(e) => setPasswordRepeat(e.target.value)}
            />
            {error && (
              <Typography color="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {t('register.register')}
            </Button>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                {t('register.loginMessage')}
              </Typography>
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default RegisterPage;
